import { FC, use, useEffect, useState } from "react";
import ModalDefault from "../../../components/ModalDefault/ModalDefault";
import { BvIcon } from "bevi-icon";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { ReactComponent as AddCardIcon } from '../../../assets/img/addCard.svg';
import { ReactComponent as VisaCardIcon } from '../../../assets/img/visaCard.svg';
import { ReactComponent as McCardIcon } from '../../../assets/img/mcCard.svg';
import { ReactComponent as AeCardIcon } from '../../../assets/img/aeCard.svg';
import { ReactComponent as EloCardIcon } from '../../../assets/img/eloCard.svg';
import { ReactComponent as HpCardIcon } from '../../../assets/img/hcCard.svg';
import "./General.scss";
import { useNavigate } from "react-router-dom";
import { UserRoleEnum } from "../../../enums/User/UserRoleEnum";
import MainNavbar from "../../../components/MainNavbar/MainNavbar";
import ProfileLayout from "../../../components/ProfileLayout/ProfileLayout";
import { PaymentsService } from "../../../services/Financial/Payments/PaymentsServices";
import { ProfileService } from "../../../services/Profile/ProfileService";
import { toast } from "react-toastify";
import { useAuth } from "../../../providers/AuthProvider";
import { Card, Col, Row } from "react-bootstrap";
import { size } from "@floating-ui/dom";

interface GeneralProps {}

interface Payment {
  id: number;
  amount_paid: string;
  payment_date: string;
  plans_payments_methods: { id: number; name: string };
  plans_payments_status: { id: number; name: string };
  invoice_pdf: string | null;
}

interface Pagination {
  current: number;
  next: number | null;
  last: number;
  total: number;
}

interface ActiveSubscription {
  subscription_id: number;
  plan_name: string;
  end_date: string;
  user_id: number;
  canceled: boolean;
}

interface PaymentMethod {
  id: number;
  card_brand: string;
  last_digits: string;
  is_default: number;
  created_at: string;
}

const paymentsService = new PaymentsService();
const profileService = new ProfileService();

interface AddCardFormProps {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddCardFormInner: FC<AddCardFormProps> = ({ show, onClose, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [titular, setTitular] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [paymentType, setPaymentType] = useState<"credit" | "debit">("credit");

  const handleAdd = async () => {
    if (!stripe || !elements) return;
    const cardElement = elements.getElement(CardNumberElement);
    if (!cardElement) return;

    setIsAdding(true);
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
      billing_details: { name: titular },
    });

    if (error) {
      toast.error(error.message);
      setIsAdding(false);
      return;
    }

    const [, apiError] = await profileService.addPaymentMethod({
      stripe_payment_method_id: paymentMethod.id,
      is_default: false,
      card_type: paymentType,
    });
    setIsAdding(false);

    if (apiError) {
      toast.error("Erro ao adicionar cartão.");
      return;
    }

    toast.success("Cartão adicionado com sucesso!");
    setTitular("");
    setPaymentType("credit");
    onSuccess();
    onClose();
  };

  return (
    <ModalDefault
      className="modal-add-card"
      title="Adicionar cartão"
      icon="creditCard"
      show={show}
      onClose={onClose}
      sizeModal="md"
      buttonText="Adicionar cartão"
      handleSubmit={handleAdd}
      disabledSubmit={isAdding}
      showButtonSpinner={isAdding}
      showCloseButton={false}
      showCloseIcon={true}
    >
      <div className="add-card-form">
        <div className="mb-3">
          <label className="add-card-label">Escolha a forma de pagamento:</label>
          <Row className="mt-2">
            <Col xs={6}>
              <Card
                className={`payment-type-card ${paymentType === "credit" ? "active" : ""}`}
                onClick={() => setPaymentType("credit")}
              >
                <div className="d-flex align-items-center">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={paymentType === "credit"}
                      onChange={() => setPaymentType("credit")}
                    />
                    <label className="form-check-label">Cartão de Crédito</label>
                  </div>
                  <BvIcon name="creditCard" variant="duo" width={32} height={32} className="ms-2" />
                </div>
              </Card>
            </Col>
            <Col xs={6}>
              <Card
                className={`payment-type-card ${paymentType === "debit" ? "active" : ""}`}
                onClick={() => setPaymentType("debit")}
              >
                <div className="d-flex align-items-center">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={paymentType === "debit"}
                      onChange={() => setPaymentType("debit")}
                    />
                    <label className="form-check-label">Cartão de Débito</label>
                  </div>
                  <BvIcon name="creditCard" variant="solid" width={32} height={32} className="ms-2" />
                </div>
              </Card>
            </Col>
          </Row>
          {paymentType === 'debit' && (
            <div className="debit-warning mt-2 d-flex align-items-center gap-2">
              <BvIcon name="info" variant="light" width={24} height={24} color="#222343" />
              <span style={{ fontSize: "12px",fontWeight: 400, color: "#222343" }}>Cartões com função débito/crédito serão cobrados na função crédito.</span>
            </div>
          )}
        </div>
        <div className="form-group mb-3">
          <label className="add-card-label">Número do cartão</label>
          <CardNumberElement
            className="form-control"
            options={{ style: { base: { color: "#222343", fontSize: "16px", "::placeholder": { color: "#aab7c4" } } } }}
            onChange={(e) => { if (e.error) console.log(e.error.message); }}
          />
        </div>
        <div className="form-group mb-3">
          <label className="add-card-label">Nome do titular do cartão</label>
          <input
            className="form-control"
            type="text"
            placeholder="Inserir nome completo"
            value={titular}
            onChange={(e) => setTitular(e.target.value)}
          />
        </div>
        <div className="d-flex gap-3">
          <div className="form-group flex-fill">
            <label className="add-card-label">Data de Validade</label>
            <CardExpiryElement
              className="form-control"
              options={{ style: { base: { color: "#222343", fontSize: "16px", "::placeholder": { color: "#aab7c4" } } } }}
              onChange={(e) => { if (e.error) console.log(e.error.message); }}
            />
          </div>
          <div className="form-group flex-fill">
            <label className="add-card-label">CVV</label>
            <CardCvcElement
              className="form-control"
              options={{ style: { base: { color: "#222343", fontSize: "16px", "::placeholder": { color: "#aab7c4" } } } }}
              onChange={(e) => { if (e.error) console.log(e.error.message); }}
            />
          </div>
        </div>
      </div>
    </ModalDefault>
  );
};

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString("pt-BR");
};

const formatAmount = (amount: string) => {
  return parseFloat(amount).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

const General: FC<GeneralProps> = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [isLoadingMethods, setIsLoadingMethods] = useState(true);
  const [showAllMethods, setShowAllMethods] = useState(false);
  const [activeSubscription, setActiveSubscription] = useState<ActiveSubscription | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const [modalMode, setModalMode] = useState<'default' | 'remove'>('default');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const { profileImageUrl, userLogged } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (userLogged && userLogged.role !== UserRoleEnum.ADMIN) {
      navigate("/", { replace: true });
    }
  }, [userLogged]);

  const METHODS_VISIBLE = 2;

  const fetchPayments = async (page = 1, append = false) => {
    if (page === 1) setIsLoading(true);
    else setIsLoadingMore(true);

    const [response, error] = await paymentsService.listUserPayments({ page, perPage: 3 });

    if (page === 1) setIsLoading(false);
    else setIsLoadingMore(false);

    if (error || !response) {
      toast.error("Erro ao carregar histórico de pagamentos.");
      return;
    }

    const items: Payment[] = response?.data?.items ?? [];
    const pag: Pagination = response?.data?.pagination;

    setPayments((prev) => (append ? [...prev, ...items] : items));
    setPagination(pag);
  };

  const fetchPaymentMethods = async () => {
    setIsLoadingMethods(true);
    const [response, error] = await profileService.listPaymentMethods();
    setIsLoadingMethods(false);

    if (error || !response) {
      toast.error("Erro ao carregar formas de pagamento.");
      return;
    }

    const sorted = (response?.data ?? []).sort((a: PaymentMethod, b: PaymentMethod) => {
      if (b.is_default !== a.is_default) return b.is_default - a.is_default;
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    });
    setPaymentMethods(sorted);
  };

  const fetchActiveSubscription = async () => {
    const [response, error] = await profileService.getActiveSubscription();
    if (error || !response) return;
    setActiveSubscription(response?.data ?? null);
  };

  useEffect(() => {
    fetchPayments(1);
    fetchPaymentMethods();
    fetchActiveSubscription();
  }, []);

  const handleLoadMore = () => {
    if (!pagination?.next) return;
    fetchPayments(pagination.next, true);
  };

  const handleModalSubmit = async () => {
    if (!selectedMethod) return;
    setIsSubmitting(true);

    if (modalMode === 'default') {
      const [, error] = await profileService.setDefaultPaymentMethod(selectedMethod.id);
      setIsSubmitting(false);
      if (error) { toast.error("Erro ao definir cartão principal."); return; }
      toast.success("Cartão principal atualizado com sucesso!");
    } else {
      const [, error] = await profileService.deletePaymentMethod(selectedMethod.id);
      setIsSubmitting(false);
      if (error) { toast.error("Erro ao remover a forma de pagamento."); return; }
      toast.success("Forma de pagamento removida com sucesso!");
    }

    setSelectedMethod(null);
    fetchPaymentMethods();
  };

  return (
    <>
      <MainNavbar />
      <div className="General" data-testid="General">
        <div className="account-card">
          <ProfileLayout profileImageUrl={profileImageUrl}>
            <div className="general-content-inner">
              <h5 className="mb-4 general-title">Geral</h5>
              <div className="general-layout">
                {/* Main column */}
                <div className="general-main">
                  {/* Histórico de pagamentos */}
                  <section className="general-section">
                    <div className="general-section-title">Histórico de pagamentos</div>
                    <div className="general-section-desc" style={{ paddingBottom: "25px" }}>
                      Gerencie informações de cobrança e visualize seus recibos
                    </div>

                    {isLoading ? (
                      <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Carregando...</span>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="general-payments-scroll mt-3">
                          {payments.length === 0 ? (
                            <p className="text-muted">Não há histórico de pagamento</p>
                          ) : (
                            <table className="general-table w-100">
                              <thead>
                                <tr>
                                  <th>Data de<br/>Pagamento</th>
                                  <th>Forma de<br/>Pagamento</th>
                                  <th>Valor da<br/>Cobrança</th>
                                  <th>Recibo de<br/>Pagamento</th>
                                </tr>
                              </thead>
                              <tbody>
                                {payments.map((p) => (
                                  <tr key={p.id}>
                                    <td>{formatDate(p.payment_date)}</td>
                                    <td>{p.plans_payments_methods.name}</td>
                                    <td>{formatAmount(p.amount_paid)}</td>
                                    <td>
                                      {p.invoice_pdf ? (
                                        <a
                                          href={p.invoice_pdf}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="btn-download"
                                        >
                                          Download
                                        </a>
                                      ) : (
                                        <span className="text-muted" style={{ fontSize: "0.8rem" }}>—</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>

                        {pagination?.next && (
                          <button
                            className="general-link mt-3 d-inline-block"
                            onClick={handleLoadMore}
                            disabled={isLoadingMore}
                          >
                            {isLoadingMore ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" />
                                Carregando...
                              </>
                            ) : (
                              "Ver mais recibos"
                            )}
                          </button>
                        )}
                      </>
                    )}
                  </section>

                  {/* Forma de Pagamento */}
                  <section className="general-section mt-4">
                    <div className="general-section-title">Forma de Pagamento</div>
                    <div className="general-section-desc">
                      Altere ou remova seu cartão de cobrança.
                    </div>

                    {isLoadingMethods ? (
                      <div className="text-center py-3">
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Carregando...</span>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className={`mt-3 d-flex flex-column gap-2${showAllMethods ? " general-payments-scroll" : ""}`}>
                          {(showAllMethods ? paymentMethods : paymentMethods.slice(0, METHODS_VISIBLE)).map((pm) => (
                            <div key={pm.id} className="payment-method-row">
                              <div className="d-flex align-items-center gap-3">
                                <div className="card-brand-icon">
                                  {{
                                    visa: <VisaCardIcon width={34} height={34} />,
                                    mastercard: <McCardIcon width={34} height={34} />,
                                    amex: <AeCardIcon width={34} height={34} />,
                                    elo: <EloCardIcon width={34} height={34} />,
                                    hipercard: <HpCardIcon width={34} height={34} />,
                                  }[pm.card_brand.toLowerCase()] ?? (
                                    <BvIcon name="creditCard" variant="solid" weight={600} width={34} height={34} />
                                  )}
                                </div>
                                <span className="payment-method-number">
                                  **** **** **** {pm.last_digits}
                                </span>
                              </div>
                              {pm.is_default === 1 ? (
                                <span className="badge-principal">Principal</span>
                              ) : (
                                <div className="d-flex align-items-center gap-2">
                                  <button className="btn-icon" title="Configurações" onClick={() => { setModalMode('default'); setSelectedMethod(pm); }}>
                                    <BvIcon name="settings" variant="solid" width={24} height={24} />
                                  </button>
                                  <button className="btn-icon btn-icon--danger" title="Remover" onClick={() => { setModalMode('remove'); setSelectedMethod(pm); }}>
                                    <BvIcon name="trashDelete" variant="solid" width={24} height={24} />
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                          {paymentMethods.length === 0 && (
                            <span className="text-muted" style={{ fontSize: "16px" }}>
                              Nenhuma forma de pagamento cadastrada.
                            </span>
                          )}
                        </div>

                        <div className="payment-method-row payment-method-add mt-2" onClick={() => setShowAddCard(true)}>
                          <div className="d-flex align-items-center gap-3">
                            <AddCardIcon/>
                            <span className="payment-method-add-label">Adicionar cartão</span>
                          </div>
                        </div>

                        {paymentMethods.length > METHODS_VISIBLE && !showAllMethods && (
                          <button
                            className="general-link mt-3 d-inline-block"
                            onClick={() => setShowAllMethods(true)}
                          >
                            Ver mais formas de pagamento
                          </button>
                        )}

                        <p className="payment-method-info mt-3">
                          Cartão principal é utilizado para realizar a renovação da sua assinatura. Caso o cartão principal, por algum motivo, não passe a transação, será feita uma nova tentativa desta vez usando o cartão adicional.
                        </p>
                      </>
                    )}
                  </section>

                  {activeSubscription && !activeSubscription.canceled && (
                    <button
                      className="general-link-danger mt-4 d-inline-block"
                      onClick={() => window.open("/cancelation/reason", "_blank")}
                    >
                      Cancelar assinatura
                    </button>
                  )}
                </div>

                {/* Plano Ativo card */}
                <div className="general-plan-card">
                  <div className={`plan-label`}>
                    {!activeSubscription ? "Não há plano ativo" : activeSubscription.canceled ? "Plano Cancelado" : "Plano Ativo"}
                  </div>
                  <div className={`plan-name`}>{activeSubscription?.plan_name ?? ""}</div>
                  {activeSubscription?.end_date && (
                    <div className="plan-renewal">
                      {activeSubscription.canceled ? "Encerra em " : "Renova em "}
                      {new Date(activeSubscription.end_date).toLocaleDateString("pt-BR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })}.
                    </div>
                  )}
                  <button
                    className="btn-change-plan"
                    disabled={!!activeSubscription?.canceled}
                    onClick={!activeSubscription ? () => window.open("/pricing/list", "_blank") : undefined}
                  >
                    {activeSubscription ? "Trocar de Plano" : "Assinar Plano"}
                  </button>
                </div>
              </div>
            </div>
          </ProfileLayout>
        </div>
      </div>

      <AddCardFormInner
        show={showAddCard}
        onClose={() => setShowAddCard(false)}
        onSuccess={fetchPaymentMethods}
      />

      <ModalDefault
        className="modal-set-default"
        title={modalMode === 'remove' ? "Remover cartão" : "Mudar cartão principal"}
        icon={modalMode === 'remove' ? "trashDelete" : "settings"}
        show={!!selectedMethod}
        onClose={() => setSelectedMethod(null)}
        sizeModal="md"
        buttonText={modalMode === 'remove' ? "Sim, Remover" : "Sim, tornar principal"}
        handleSubmit={handleModalSubmit}
        disabledSubmit={isSubmitting}
        showButtonSpinner={isSubmitting}
        showCloseButton={false}
        showCloseIcon                
      >
        <p style={{ color: "#62618F", fontSize: "14px", marginBottom: "1rem" }}>
          {modalMode === 'remove' ? "Deseja remover esse cartão?" : "Tornar este cartão como principal?"}
        </p>
        {selectedMethod && (
          <div className="payment-method-row">
            <span className="payment-method-number">
              **** **** **** {selectedMethod.last_digits}
            </span>
            <div className="card-brand-icon">
              {{
                visa: <VisaCardIcon width={34} height={34} />,
                mastercard: <McCardIcon width={34} height={34} />,
                amex: <AeCardIcon width={34} height={34} />,
                elo: <EloCardIcon width={34} height={34} />,
                hipercard: <HpCardIcon width={34} height={34} />,
              }[selectedMethod.card_brand.toLowerCase()] ?? (
                <BvIcon name="creditCard" variant="solid" weight={600} width={34} height={34} />
              )}
            </div>
          </div>
        )}
      </ModalDefault>
    </>
  );
};

export default General;
